import { z } from 'zod';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { organization } from '@trashlab/api/src/domains/identity/schemas/organization';
import {
  pgEnum,
  pgTable,
  varchar,
  boolean,
  index,
  foreignKey,
} from 'drizzle-orm/pg-core';

// Zod schemas for validation
export const websiteTemplateSchema = z.enum([
  'DEFAULT',
  'SECONDARY',
  'REDEBINS',
]);

export const organizationWebsiteCreateSchema = z.object({
  tenantId: z.uuid(),
  template: websiteTemplateSchema.default('DEFAULT'),
  enableEcommerce: z.boolean().optional().default(true),
  enableAiChat: z.boolean().optional().default(true),
  enableGoogleReviews: z.boolean().optional().default(true),
  enableQuickQuote: z.boolean().optional().default(true),
  googlePlaceId: z.string().max(255).optional(),
  googleAnalyticsId: z.string().max(255).optional(),
  googleTagManagerId: z.string().max(255).optional(),
  enableGoogleAnalytics: z.boolean().optional().default(false),
  showPortableToiletSubscriptionRates: z.boolean().optional().default(true),
  showResidentialSubscriptionRates: z.boolean().optional().default(true),
  showCommercialSubscriptionRates: z.boolean().optional().default(true),
});

export const organizationWebsiteUpdateSchema = z.object({
  id: z.uuid(),
  fieldsToUpdate: z.object({
    template: websiteTemplateSchema.optional(),
    enableEcommerce: z.boolean().optional(),
    enableAiChat: z.boolean().optional(),
    enableGoogleReviews: z.boolean().optional(),
    enableQuickQuote: z.boolean().optional(),
    googlePlaceId: z.string().max(255).optional(),
    googleAnalyticsId: z.string().max(255).optional(),
    googleTagManagerId: z.string().max(255).optional(),
    enableGoogleAnalytics: z.boolean().optional(),
    showPortableToiletSubscriptionRates: z.boolean().optional(),
    showResidentialSubscriptionRates: z.boolean().optional(),
    showCommercialSubscriptionRates: z.boolean().optional(),
  }),
});

export const organizationWebsiteQuerySchema = z.object({
  id: z.uuid().optional(),
  tenantId: z.uuid().optional(),
});

// Type exports
export type OrganizationWebsiteCreate = z.infer<
  typeof organizationWebsiteCreateSchema
>;
export type OrganizationWebsiteUpdate = z.infer<
  typeof organizationWebsiteUpdateSchema
>;
export type OrganizationWebsiteQuery = z.infer<
  typeof organizationWebsiteQuerySchema
>;
export type WebsiteTemplate = z.infer<typeof websiteTemplateSchema>;

// Legacy type compatibility (for existing code)
export type OrganizationWebsiteSchema = {
  id: string;
  tenantId: string;
  createdAtTs: Date;
  updatedAtTs: Date;
  versionNumber: number | null;
  template: WebsiteTemplate;
  enableEcommerce: boolean | null;
  enableAiChat: boolean | null;
  enableGoogleReviews: boolean | null;
  enableQuickQuote: boolean | null;
  googlePlaceId: string | null;
  googleAnalyticsId: string | null;
  googleTagManagerId: string | null;
  enableGoogleAnalytics: boolean | null;
  showPortableToiletSubscriptionRates: boolean | null;
  showResidentialSubscriptionRates: boolean | null;
  showCommercialSubscriptionRates: boolean | null;
};

export type WebsiteTemplateSchema = WebsiteTemplate;

// Drizzle table schemas (kept for foreign key references and backwards compatibility)
export const website_template = pgEnum('website_template', [
  'DEFAULT',
  'SECONDARY',
  'REDEBINS',
]);

export const organization_website = pgTable(
  'organization_website',
  {
    ...defaultFields.fieldsWithTenantId(),
    template: website_template('template').default('DEFAULT').notNull(),
    enableEcommerce: boolean('enableEcommerce').default(true),
    enableAiChat: boolean('enableAiChat').default(true),
    enableGoogleReviews: boolean('enableGoogleReviews').default(true),
    enableQuickQuote: boolean('enableQuickQuote').default(true),
    googlePlaceId: varchar('googlePlaceId', { length: 255 }).unique(),
    googleAnalyticsId: varchar('googleAnalyticsId', { length: 255 }),
    googleTagManagerId: varchar('googleTagManagerId', { length: 255 }),
    enableGoogleAnalytics: boolean('enableGoogleAnalytics').default(false),
    showPortableToiletSubscriptionRates: boolean(
      'showPortableToiletSubscriptionRates',
    ).default(true),
    showResidentialSubscriptionRates: boolean(
      'showResidentialSubscriptionRates',
    ).default(true),
    showCommercialSubscriptionRates: boolean(
      'showCommercialSubscriptionRates',
    ).default(true),
  },
  (table) => ({
    createdAtTs_idx: index('organization_website_createdAtTs_idx').on(
      table.createdAtTs,
    ),
    tenantId_idx: index('organization_website_tenantId_idx').on(table.tenantId),
    updatedAtTs_idx: index('organization_website_updatedAtTs_idx').on(
      table.updatedAtTs,
    ),
    updatedAtTs_idx1: index('organization_website_updatedAtTs_idx1').on(
      table.updatedAtTs.desc(),
    ),
    versionNumber_idx: index('organization_website_versionNumber_idx').on(
      table.versionNumber,
    ),
    googlePlaceId_idx: index('organization_website_googlePlaceId_idx').on(
      table.googlePlaceId,
    ),

    organizationId_fkey: foreignKey({
      columns: [table.tenantId],
      foreignColumns: [organization.id],
      name: 'organization_website_organizationId_fkey',
    }),
  }),
);

export const organization_website_history = pgTable(
  'organization_website_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    template: website_template('template'),
    enableEcommerce: boolean('enableEcommerce'),
    enableAiChat: boolean('enableAiChat'),
    enableGoogleReviews: boolean('enableGoogleReviews'),
    enableQuickQuote: boolean('enableQuickQuote'),
    googlePlaceId: varchar('googlePlaceId', { length: 255 }),
    googleAnalyticsId: varchar('googleAnalyticsId', { length: 255 }),
    googleTagManagerId: varchar('googleTagManagerId', { length: 255 }),
    enableGoogleAnalytics: boolean('enableGoogleAnalytics'),
    showPortableToiletSubscriptionRates: boolean(
      'showPortableToiletSubscriptionRates',
    ),
    showResidentialSubscriptionRates: boolean(
      'showResidentialSubscriptionRates',
    ),
    showCommercialSubscriptionRates: boolean('showCommercialSubscriptionRates'),
  },
);
